import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {LineSettingDto} from '../../../../generated-model/models';
import {LineSettingService} from './line-setting-service';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';

const LINE_USER_ID_PATTERN = /^U[a-fA-F0-9]{32}$/;

@Component({
  selector: 'app-line-setting',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PageTitleComponent],
  templateUrl: './line-setting.html',
  styleUrl: './line-setting.scss',
})
export class LineSettingComponent implements OnInit {
  private readonly service = inject(LineSettingService);
  private readonly fb = inject(FormBuilder);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  readonly webhookUrl = `${environment.baseUrl.replace(/\/+$/, '')}/public/api/line/request/webhook`;
  readonly isLoading = signal(true);
  readonly isSubmitting = signal(false);
  submitted = false;

  readonly form: FormGroup = this.fb.group({
    id: [1],
    channelSecret: ['', Validators.required],
    channelAccessToken: ['', Validators.required],
    testGroupId: ['', Validators.required],
    testGroupName: ['', Validators.required],
    prodGroupId: ['', Validators.required],
    prodGroupName: ['', Validators.required],
    forwardUrl: [''],
    allowedUsers: this.fb.array([], this.duplicateUserIdValidator),
  });

  get allowedUsers(): FormArray {
    return this.form.get('allowedUsers') as FormArray;
  }

  ngOnInit(): void {
    this.service.getSetting().subscribe({
      next: (data: LineSettingDto) => {
        this.form.patchValue(data);
        data.allowedUsers?.forEach(user => this.allowedUsers.push(this.createAllowedUser(user.name, user.userId)));
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  addAllowedUser(): void {
    this.allowedUsers.push(this.createAllowedUser());
  }

  removeAllowedUser(index: number): void {
    this.allowedUsers.removeAt(index);
  }

  async save(): Promise<void> {
    this.submitted = true;
    if (this.form.invalid || this.isSubmitting()) {
      return;
    }
    const confirmed = await this.confirmDialogService.confirmSave();
    if (!confirmed) {
      return;
    }
    this.isSubmitting.set(true);
    try {
      await firstValueFrom(this.service.saveSetting(this.form.value));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private createAllowedUser(name = '', userId = ''): FormGroup {
    return this.fb.group({
      name: [name, Validators.required],
      userId: [userId, [Validators.required, Validators.pattern(LINE_USER_ID_PATTERN)]],
    });
  }

  private duplicateUserIdValidator(control: AbstractControl): {[key: string]: boolean} | null {
    const userIds = (control.value as Array<{userId?: string}>).map(user => user.userId?.trim()).filter(Boolean);
    return new Set(userIds).size === userIds.length ? null : {duplicateUserId: true};
  }
}
